import { Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import LoadingAnimation from "../../components/loading/loading";
import { useState, useEffect, useMemo } from "react";
import { CompanyAPI } from '../../api/company/company-api-factory';
import { CompanyContainer } from '../../components/company/company-container';
import { arrayHasMoreThanNItems, arrayHasNItems } from '../../utils/arrayUtils';
import UserApi from '../../api/user/user-api-factory';
import { useNavigate } from "react-router-dom";
import { AddCompanyModal } from '../../components/company/add-company-modal';
import { useUserProfile } from '../../app';

import './../properties/property.css';

// Map backend apiRoles (e.g. "tenant_Manager") to display roles
// consistent with menus (e.g. "Tenant.Manager")
const mapApiRoleToDisplayRole = apiRole => {
    const roleMap = {
        tenant_Manager: 'Tenant.Manager',
        user_Basic: 'User.Basic',
        billing_Admin: 'Billing.Admin',
        application_Developer: 'Application.Developer',
    };
    return roleMap[apiRole] || apiRole;
};

export const CompanySearch = () => {
    const navigate = useNavigate();
    const { instance, inProgress, accounts } = useMsal();
    const profile = useUserProfile();
    const userApi = useMemo(() => new UserApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);
    const api = useMemo(() => new CompanyAPI(instance, inProgress, accounts, true), [instance, inProgress, accounts]);

    const authorized = useMemo(() => {
        const primaryAccount = accounts && accounts[0];
        const aadRoles = primaryAccount?.idTokenClaims?.roles || [];
        const apiRoles = profile?.systemProfile?.apiRoles || [];
        const mappedApiRoles = apiRoles.length ? apiRoles.map(mapApiRoleToDisplayRole) : [];
        const roles = mappedApiRoles.length ? mappedApiRoles : aadRoles;

        return arrayHasMoreThanNItems(accounts, 0) &&
            roles && roles.length &&
            (userApi.hasRole(roles, 'Application.Developer') ||
                userApi.hasRole(roles, 'Tenant.Manager'));
    }, [accounts, userApi, profile]);

    const [companyData, setCompanyData] = useState(null);
    const [fetchInProgress, setFetchInProgress] = useState(false);
    const [shouldFetch, setShouldFetch] = useState(false);
    const [showCompanyDelete, setShowCompanyDelete] = useState(false);
    const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
    const [selectedCompanies, setSelectedCompanies] = useState([]);

    const fetchCompanies = () => {
        if (authorized) {
            setFetchInProgress(true);

            api.getAll()
                .then(data => {
                    setCompanyData(data);
                })
                .catch(error => {
                    console.log("An error occurred getting data from the Company Data API.", error);
                    setCompanyData(null);
                })
                .finally(() => {
                    setShouldFetch(false);
                    setFetchInProgress(false);
                });
        }
    };

    useEffect(() => {
        if (authorized) {
            setShouldFetch(true);
        }
    }, [authorized]);

    useEffect(() => {
        if (authorized && shouldFetch && !fetchInProgress) {
            fetchCompanies();
        }
    }, [shouldFetch, fetchInProgress, authorized]);

    const handleAddCompany = () => {
        setShowAddCompanyModal(true);
    };

    const handleCompanyAdded = () => {
        setShowAddCompanyModal(false);
        setShouldFetch(true); // Trigger a refresh of the company list
    };

    const toggleCompanyDelete = () => {
        setShowCompanyDelete(!showCompanyDelete);
        // Clear selected companies when toggling delete mode off
        if (showCompanyDelete) {
            setSelectedCompanies([]);
        }
    };

    // First, let's fix the handleToggleDelete function in company-search.jsx
    const handleToggleDelete = (company) => {
        console.log("Toggle delete for company:", company.name, "checked:", company.toDelete);

        setSelectedCompanies(prev => {
            if (company.toDelete) {
                // Add to selected companies if not already there
                if (!prev.some(c => c.id === company.id)) {
                    return [...prev, company];
                }
            } else {
                // Remove from selected companies
                return prev.filter(c => c.id !== company.id);
            }
            return prev;
        });
    };


    const handleDeleteSelected = () => {
        if (selectedCompanies.length === 0) return;

        // Here you would call your API to delete the selected companies
        // For now, let's just log them
        console.log("Companies to delete:", selectedCompanies);

        // After successful deletion, you would refresh the list
        // setShouldFetch(true);
    };

    if (arrayHasNItems(companyData, 1)) {
        return navigate(`/company/detail/${companyData[0].id}`);
    }

    // Determine the delete button text based on selection count
    const deleteButtonText = selectedCompanies.length === 1
        ? "Delete Selected Company"
        : `Delete Selected Companies (${selectedCompanies.length})`;

    return (
        <>
            {arrayHasMoreThanNItems(companyData, 1) && <ol className="breadcrumb float-xl-end">
                <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                <li className="breadcrumb-item"><Link to="/company/search">Company Search</Link></li>
                <li className="breadcrumb-item active">Results</li>
            </ol>}
            <h1 className="page-header">Manage Companies | <small>manage the Companies associated with your account</small></h1>
            <div>
                <button type="button" className="btn btn-primary" data-bs-toggle="dropdown">
                    Action&nbsp;&nbsp;<span className="caret"></span>
                </button>
                <div className="dropdown-menu">
                    <div className='dropdown-item link-like-div' onClick={handleAddCompany}>
                        <i className='fa fa-plus fa-lg'></i>&nbsp;&nbsp;Add Company
                    </div>
                    <div className='dropdown-item link-like-div' onClick={toggleCompanyDelete}>
                        <i className='fa fa-x fa-lg'></i>&nbsp;&nbsp;{`${showCompanyDelete ? 'Cancel Delete' : 'Delete Companies'}`}
                    </div>
                    {showCompanyDelete && selectedCompanies.length > 0 &&
                        <div className='dropdown-item link-like-div' onClick={handleDeleteSelected}>
                            <i className='fa fa-trash fa-lg'></i>&nbsp;&nbsp;{deleteButtonText}
                        </div>
                    }
                    {/* <div className="dropdown-divider"></div>
                    <div className='dropdown-item link-like-div'>
                        <i className='fa fa-cog fa-lg'></i>&nbsp;&nbsp;Something worth dividing
                    </div> */}
                </div>
            </div>
            {fetchInProgress && <LoadingAnimation />}
            {!fetchInProgress && companyData &&
                <CompanyContainer
                    companies={companyData}
                    showDelete={showCompanyDelete}
                    onToggleDelete={handleToggleDelete}
                />
            }
            {!fetchInProgress && !companyData && <>Nothing to show... you may not have permission to view this data</>}

            <AddCompanyModal
                isOpen={showAddCompanyModal}
                onClose={() => setShowAddCompanyModal(false)}
                onSave={handleCompanyAdded}
                api={api}
            />
        </>
    );
};